import React, { useState } from "react";
import { Calendar, ChevronDown, Clock4, User } from "lucide-react";
import { formatDate, handleStopPropagation } from "components/common/Utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  cloneSpace,
  deleteWorkspace,
  updateWorkspaceStatus,
} from "store/actions/workspaceSlice";
import { showAlert } from "store/reducers/alertReducer";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import ClosureDetailsTooltip from "../Workspace/ClosureDetailsTooltip";
import LockIcon from "assets/images/icons/lock.svg";
import Riyal from "assets/images/icons/riyal.svg";

const WorkshopCard = ({ item, className = "", isLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const handleDraft = (id, status) => {
    const updatedStatus = status === 1 ? 3 : 1;
    const payload = {
      id,
      status: updatedStatus, // 3 FOR DRAFT, 1 FOR PUBLISH
    };
    dispatch(updateWorkspaceStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleStatusChange = (id, status) => {
    const updatedStatus = status;
    const payload = {
      id,
      status: updatedStatus, // 4 FOR UNAVAILABLE, 5 FOR ARCHIVE
    };
    dispatch(updateWorkspaceStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteWorkspace(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleDuplicate = (id) => {
    const payload = {
      id,
    };
    dispatch(cloneSpace(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <>
      <div
        className={`inner_section c_p ${className}`}
        onClick={() => {
          navigate(`/bookings/workshops/workshop-details?id=${item._id}`);
        }}
      >
        <img
          src={item?.imageUrls[0]}
          className="workshop_img"
          alt=""
          width={230}
          height={168}
        />
        <div className="content w-100">
          <div className="left_section">
            {/* TOP ROW */}
            <div className="g_between align-items-start">
              <div>
                <div className="d-flex gap-2">
                  <h2 className="fs_20">{item.name || "-"}</h2>
                  {item?.closureDetails && (
                    <div
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <img src={LockIcon} alt="lock" width={18} height={19} />

                      {showTooltip && (
                        <ClosureDetailsTooltip
                          item={item}
                          setShowTooltip={setShowTooltip}
                        />
                      )}
                    </div>
                  )}
                </div>

                <p className="fs_14 text_dark_gray">
                  {item?.cityId?.name || "-"}
                </p>
              </div>
              <p
                className={
                  item.status === 1
                    ? "available"
                    : item.status === 5
                    ? "archived"
                    : "draft"
                }
              >
                {item?.numberOfSeatsAvailable}{" "}
                {item.status === 1
                  ? t("Available")
                  : item.status === 4
                  ? t("Unavailable")
                  : item.status === 5
                  ? t("Archived")
                  : t("Drafted")}
              </p>
            </div>
            {/* MID ROW */}
            <div className="g_between align-items-start flex-wrap mb-1 gap-3">
              <div
                className="d-flex justify-content-between flex-column "
                style={{ minWidth: 50 }}
              >
                <p className="fs_12 text_black mt-1 d-flex gap-2 align-items-center">
                  <span className="w_fit_content">
                    <Calendar
                      width={14}
                      height={14}
                      className="text_gray icon"
                    />
                    {item?.selectedDateAndTimes?.length > 0 &&
                      formatDate(
                        item?.selectedDateAndTimes[0]?.startDate || ""
                      )}
                  </span>
                  <span className="w_fit_content">
                    <Clock4 width={14} height={14} className="text_gray icon" />
                    {item?.selectedDateAndTimes?.length > 0 && (
                      <>
                        {item?.selectedDateAndTimes[0]?.startTime?.start || ""}{" "}
                        - {item?.selectedDateAndTimes[0]?.startTime?.end || ""}
                      </>
                    )}
                  </span>
                  <span className="w_fit_content">
                    <User width={14} height={14} className="text_gray icon" />
                    {item?.workShopDetails?.numberOfSeats} {t("Seats")}
                  </span>
                  <span className="w_fit_content">
                    <User width={14} height={14} className="text_gray icon" />
                    {item?.seatsRemaining} {t("Remaining Seats")}
                  </span>
                </p>
              </div>
              <div className="g_end gap-4">
                <p className="text_primary fs_12">
                  {t("Created on:")}{" "}
                  <span className="text_dark_gray">
                    {formatDate(item?.publishedAt)}
                  </span>{" "}
                  <br />
                  <span className="text_dark_gray">{t("by")}</span>{" "}
                  {item?.createdByType === 1
                    ? item?.createdByAdmin?.name
                    : item?.createdBy?.name}
                </p>
                <p className="text_blue fs_12">
                  {t("Last Edit on:")}{" "}
                  <span className="text_dark_gray">
                    {" "}
                    {formatDate(item?.updatedAt)}
                  </span>{" "}
                  <br />
                  <span className="text_dark_gray">{t("by")}</span>{" "}
                  {item?.lastEditedBy?.name || "-"}
                </p>
              </div>
            </div>
            <div className="g_between align-items-end">
              <p className="price_tag">
                <img
                  src={Riyal}
                  width={14}
                  height={14}
                  alt="riyal"
                  className="me-1"
                />
                {item?.workShopDetails?.price}
              </p>
              {item.status !== 5 && (
                <Dropdown
                  className="custom_dropdown list_action_dropdown"
                  drop={"down"}
                  onClick={handleStopPropagation}
                >
                  <Dropdown.Toggle>
                    <button className="list_edit_btn">
                      {t("Edit")} <ChevronDown width={18} height={18} />
                    </button>
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end">
                    {item.status === 4 ? (
                      // Show only Archive if status = 4
                      <Dropdown.Item
                        href="#"
                        onClick={() => handleStatusChange(item?._id, 5)}
                      >
                        {t("Archive")}
                      </Dropdown.Item>
                    ) : item.status === 5 ? (
                      // No options if status = 5
                      <></>
                    ) : (
                      // Show all options if status is not 4 or 5
                      <>
                        {/* <Dropdown.Item
        href="#"
        onClick={() => handleDuplicate(item?._id)}
      >
        {t("Duplicate")}
      </Dropdown.Item> */}
                        <Dropdown.Item
                          href="#"
                          onClick={() => handleDraft(item?._id, item.status)}
                        >
                          {item.status === 1 ? t("Draft") : t("Publish")}
                        </Dropdown.Item>

                        <Dropdown.Item
                          href="#"
                          onClick={() => handleStatusChange(item?._id, 4)}
                        >
                          {t("Unavailable")}
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#"
                          onClick={() => handleStatusChange(item?._id, 5)}
                        >
                          {t("Archive")}
                        </Dropdown.Item>

                        <Dropdown.Item
                          href="#"
                          onClick={() => {
                            navigate(
                              `/bookings/workshops/workshop-details?id=${item._id}`
                            );
                          }}
                        >
                          {t("View")}
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#"
                          onClick={() => {
                            navigate(
                              `/bookings/workshops/update-workshop?id=${item._id}`
                            );
                          }}
                        >
                          {t("Edit")}
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#"
                          className="text_danger"
                          onClick={() => {
                            setSelectedId(item._id);
                            setShowModal(true);
                          }}
                        >
                          {t("Delete")}
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "Workshop",
        })}
        isLoading={isLoading}
      />
    </>
  );
};

export default WorkshopCard;
